import React, { useState, useEffect } from "react";
import "./Configuration.css";
import Select from "react-select";
import { toast } from "react-toastify";

import url from "../../../Utility/URL/url.js";
import axios from "axios";
import authHeader from "../../../headerTokenUtility/auth.header.js";
import getUserName from "../../../Utility/gettingInformation/getUsernameFromSession";

function Configuration() {
  const timezone = require("../../../Utility/timezone.js");
  const username = getUserName();
 
  const bookingPeriodArray = [
    { label: "1 Week", value: "1 Week" },
    { label: "2 Weeks", value: "2 Weeks" },
    { label: "3 Weeks", value: "3 Weeks" },
    { label: "4 Weeks", value: "4 Weeks" },
    { label: "2 Months", value: "2 Months" },
    { label: "3 Months", value: "3 Months" },
  ];

  const noticePeriodArray = [
    { label: "Minutes", value: "Minutes" },
    { label: "Hours", value: "Hours" },
    { label: "Days", value: "Days" },
    { label: "Weeks", value: "Weeks" },
  ];

  const [config, setConfig] = useState({
    timezone: null,
    meetingLink: "",
    bookingPeriod: null,
    noticePeriodValue: "",
    noticePeriodType: "",
    useGoogleMeetLink: 0, // 1 = on, 0 = off
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availabilitySave, setAvailabilitySave] = useState([])

  // helper to normalize strings for matching
  const normalize = (v) =>
    (v || "")
      .toString()
      .trim()
      .toLowerCase();

  // Fetch config
  useEffect(() => {
    let mounted = true;

    const fetchConfigDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(url.GET_CONFIG_DETAILS + username, {
          headers: authHeader(),
        });

        const data = response.data;
        if (!data) throw new Error("No data returned from config API");

        // timezone matching
        const tzOption =
          timezone.find(
            (t) => normalize(t.value) === normalize(data.timezone) || normalize(t.label) === normalize(data.timezone)
          ) || null;

        // booking period: try matching value or label, tolerant to casing/extra spaces
        const apiBooking = normalize(data.bookingPeriod || data.bookingPeriodValue || "");
        const bookingOption =
          bookingPeriodArray.find((b) => normalize(b.value) === apiBooking || normalize(b.label) === apiBooking) ||
          null;

        // notice period type/extension
        const noticeType = data.noticePeriodExtension ?? data.noticePeriodType ?? "";

        if (mounted) {
          setConfig({
            timezone: tzOption,
            meetingLink: data.personalMeetLink ?? "",
            bookingPeriod: bookingOption,
            noticePeriodValue: data.noticePeriod ?? data.noticePeriodValue ?? "",
            noticePeriodType: noticeType,
            useGoogleMeetLink: Number(data.useGoogleMeetLink ?? 0),
          });
        }
      } catch (err) {
        // better error diagnostics
        console.error("[Configuration] fetch error:", err);
        let msg = err?.message || "Failed to fetch config";
        // axios network/CORS detection
        if (err.response) {
          msg = `Server returned ${err.response.status}: ${err.response.statusText}`;
        } else if (err.request) {
          msg = "No response received. Possible network/CORS issue or server unreachable.";
        }
        if (mounted) setError(msg);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchConfigDetails();
    return () => {
      mounted = false;
    };
  }, [username]);

  // Handlers
  const handleTimezoneChange = (value) => setConfig((prev) => ({ ...prev, timezone: value }));
  const handleBookingPeriod = (value) => setConfig((prev) => ({ ...prev, bookingPeriod: value }));
  const handleNoticePeriodValue = (e) => setConfig((prev) => ({ ...prev, noticePeriodValue: e.target.value }));
  const handleNoticePeriodType = (e) => setConfig((prev) => ({ ...prev, noticePeriodType: e.target.value }));
  const handleMeetingLinkChange = (e) => setConfig((prev) => ({ ...prev, meetingLink: e.target.value }));
  const handleGoogleMeetToggle = () =>
    setConfig((prev) => ({ ...prev, useGoogleMeetLink: prev.useGoogleMeetLink === 1 ? 0 : 1 }));

 // --------- CREATE CLEAN PAYLOAD FOR API ---------
// --------- CREATE CLEAN PAYLOAD FOR API ---------
const buildPayload = () => {
  // keep only primitive values; prefer undefined for absent keys (so backend can use defaults)
  const payload = {
    timezone: typeof config.timezone === "string" ? config.timezone : config.timezone?.value,
    personalMeetLink: config.meetingLink || undefined,
    bookingPeriod: config.bookingPeriod?.value || undefined,
    noticePeriod: config.noticePeriodValue ? Number(config.noticePeriodValue) : undefined,
    noticePeriodExtension: config.noticePeriodType || undefined,
    useGoogleMeetLink: Number(config.useGoogleMeetLink ?? 0),
  };

  // remove keys that are undefined (so we don't send empty strings that may break server validation)
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined || payload[k] === "") delete payload[k];
  });

  return payload;
};

// --------- UPDATE API (with detailed logging & optional PUT fallback) ---------
const saveAvailability = async () => {
  const payload = buildPayload();
  // console.log("PAYLOAD SENT:", payload);

  // ensure JSON content-type (helps some backends)
  const headers = { ...authHeader(), "Content-Type": "application/json" };

  try {
    // Try POST first (your code used POST). If server expects PUT you can change to axios.put below.
    const res = await axios.post(url.  GET_CONFIG_UPDATE_DETAILS + username, payload, { headers });
   
    // console.info("[Configuration] save response:", res?.data ?? res);
    return res.data ?? res;
  } catch (err) {
    // Very important: log everything server returned so you can fix server-side validation issues
    console.error("", err);

    if (err.response) {
      
      // Try to provide a helpful error toast/message
      const backendMessage =
        (err.response.data && (err.response.data.message || err.response.data.error || JSON.stringify(err.response.data))) ||
        err.response.statusText;
      throw new Error(`Server ${err.response.status}: ${backendMessage}`);
    } else if (err.request) {
      // Request sent but no response
      console.error("", err.request);
     
    } else {
      // Something else happened building the request
      console.error("", err.message);
      throw new Error(err.message || "Unknown request error");
    }
  }
};

// --------- SAVE BUTTON CLICK (async/await + toast + refresh) ---------
const handleSave = async () => {
  const promise = saveAvailability();
   if (promise.status === 200) {
            setAvailabilitySave(() => promise.data);
          }
  toast.promise(promise, {
    pending: "Updating Configurations...",
    success: "Updated successfully!",
    error: {
      render({ data }) {
        // data is Error thrown above
        return `Failed to update: ${data?.message || "technical issue"}`;
      },
    },
  });

  try {
    const result = await promise; // wait so we can refresh values after a successful save
    
  } catch (err) {
    console.warn("save failed:", err.message);
    // toast already showed error via toast.promise
  }
};



  // if (loading) return <div>Loading configuration…</div>;
  // if (error) return <div className="text-danger">Error: {error}</div>;

  return (
    <div className="configuration">
      <div className="configuration-tab">
        <div className="card-header">
          <div>
            <h2>Configuration</h2>
            <p className="card-subtitle">Configure your meeting preferences</p>
          </div>
        </div>

        <div className="configuration-items">
          {/* TIMEZONE */}
          <div className="config-item">
            <div className="config-title mt-2 mb-2 d-flex flex-row align-items-start">
              <div className="config-icon "  style={{marginTop:"3px"}}>
                <i className="fa-solid fa-globe" style={{ color: "#0f172a", }}></i>
              </div>
              <div>
                <strong>Timezone</strong>
                <p className="card-subtitle" style={{marginTop:"-3px"}}>Required for accurate communication timing</p>
              </div>
            </div>
            <div className="config-input">
              <Select options={timezone} value={config.timezone} onChange={handleTimezoneChange} isSearchable />
            </div>
          </div>

          {/* MEETING LINK */}
          <div className="config-item">
            <div className="config-title mt-2 d-flex flex-row align-items-center">
              <div className="config-icon">
                <i className="fa-solid fa-link" style={{ color: "#0f172a" }}></i>
              </div>
              <div>
                <strong>Personal meeting link</strong>
              </div>
            </div>
            <div className="config-input">
              <input
                className="form-control"
                type="text"
                value={config.meetingLink}
                onChange={handleMeetingLinkChange}
                placeholder="https://meet.google.com/..."
              />
            </div>
          </div>

          {/* BOOKING PERIOD */}
          <div className="config-item">
            <div className="config-title mt-2 d-flex flex-row align-items-center">
              <div className="config-icon">
                <i className="fa-solid fa-calendar" style={{ color: "#0f172a" }}></i>
              </div>
              <div>
                <strong>Booking Period</strong>
              </div>
            </div>
            <div className="config-input">
              <Select
                options={bookingPeriodArray}
                value={config.bookingPeriod}
                onChange={handleBookingPeriod}
                isSearchable
              />
            </div>
          </div>

          {/* NOTICE PERIOD */}
          <div className="config-item">
            <div className="config-title mt-2 d-flex flex-row align-items-center">
              <div className="config-icon">
                <i className="fa-regular fa-clock" style={{ color: "#0f172a" }}></i>
              </div>
              <div>
                <strong>Notice Period</strong>
              </div>
            </div>
            <div className="config-input d-flex gap-2">
              <input
                type="number"
                className="form-control"
                value={config.noticePeriodValue}
                onChange={handleNoticePeriodValue}
                // style={{ width: 120 }}
              />
              <select
                className="form-control"
                value={config.noticePeriodType}
                onChange={handleNoticePeriodType}
                style={{ width: 120 }}
              >
                {noticePeriodArray.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* GOOGLE MEET TOGGLE */}
          <div className="config-item mt-2 pt-3 border-solid border-t">
            <div className="align-items-center config-title d-flex flex-row">
              <div className="config-icon meet">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="24" viewBox="0 0 24 24" fill="none" stroke="rgb(22 163 74)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" data-lucide="video" class="lucide lucide-video w-4"><path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"></path><rect x="2" y="6" width="14" height="12" rx="2"></rect></svg>
              </div>
              <div>
                <strong>Google Meet</strong>
                <p className="card-subtitle">Use Google Meet for your calls.</p>
              </div>
            </div>
            <div className="config-input">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={config.useGoogleMeetLink === 1}
                  onChange={handleGoogleMeetToggle}
                />
                <span className="slider-round"></span>
              </label>
            </div>
          </div>

          {/* SAVE BUTTON */}
          
        </div>
        <div className="availability-footer">
<div className="slots-save-button">
            <button className="btn btn-dark" onClick={handleSave}>
              
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Configuration;
